import { FC, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Loading } from 'react-daisyui';
import useGovernanceCheckPreview from '@/api/useGovernanceCheckPreview.ts';
import { mapGovernanceCheckPreviewToContent } from '@/pages/recommendationEngine/mapGovernanceCheckPreview.ts';
import {
  CubeTransparentIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import { FRAMEWORK_BY_ID, type FrameworkId } from '@/constants/frameworks.ts';
import type { GovernedRecommendationContent } from '@/pages/recommendationEngine/governedRecommendationTypes.ts';
import { formatConstraintSummary } from '@/utils/formatConstraintSummary.ts';
import StepCommitmentCheckpoint from '@/pages/recommendationEngine/StepCommitmentCheckpoint.tsx';
import StepGovernanceReview from '@/pages/recommendationEngine/StepGovernanceReview.tsx';
import StepCompassReview from '@/pages/recommendationEngine/StepCompassReview.tsx';
import StepFiduciaryEscalation from '@/pages/recommendationEngine/StepFiduciaryEscalation.tsx';
import StepSessionRecorded from '@/pages/recommendationEngine/StepSessionRecorded.tsx';
import {
  RECOMMENDATION_ENGINE_STEPS,
  type RecommendationEngineData,
} from '@/pages/recommendationEngine/recommendationEngineContext.ts';

const steps = RECOMMENDATION_ENGINE_STEPS;

type RecommendationEngineStep = (typeof RECOMMENDATION_ENGINE_STEPS)[number];

type RecommendationEngineLocationState = {
  decisionId?: string;
  resumeStep?: RecommendationEngineStep;
  sessionOutcome?: RecommendationEngineData['session_outcome'];
  frameworkAttribution?: string;
  governanceScore?: number | null;
};

const LOADER_STATUS_KEYS = [
  'interpretingSignals',
  'applyingFramework',
  'structuringPathways',
] as const;

const RecommendationEngine: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const routeState = location.state as RecommendationEngineLocationState | null;
  const decisionId = routeState?.decisionId;

  if (!decisionId) {
    return (
      <div className="flex w-full justify-center p-8">
        <Alert status="error">{t('recommendationEnginePage.errors.noDecision')}</Alert>
      </div>
    );
  }

  const resumeStep = routeState?.resumeStep;
  const initialStep: RecommendationEngineStep = resumeStep ?? 'recommendation';
  const initialContextData: Partial<RecommendationEngineData> = {
    decision_id: decisionId,
    loadPhase: 'interpretation-reveal',
    ...(resumeStep === 'session-recorded'
      ? {
          governance_sealed: true,
          session_outcome: routeState?.sessionOutcome,
          framework_attribution: routeState?.frameworkAttribution,
          ...(routeState?.governanceScore != null
            ? {
                governance_result: {
                  decision_id: decisionId,
                  governance_score: routeState.governanceScore ?? 0,
                  governance_status: 'pass',
                  escalation_required: false,
                  compass_filters: [],
                  risk_flags: [],
                  confidence_threshold_result: {
                    governance_score_threshold: 0.6,
                    governance_score: routeState.governanceScore ?? 0,
                    score_threshold_passed: true,
                    executive_commitment_confidence: 3,
                    human_in_loop_required: false,
                  },
                },
              }
            : {}),
        }
      : {}),
  };

  return (
    <div className="flex w-full justify-center pb-8">
      <Stepper.Wrapper
        steps={steps}
        initialStep={initialStep}
        initialContextData={initialContextData}
      >
        <Stepper.Step<typeof steps> step="recommendation">
          <StepRecommendation />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="commitment-checkpoint">
          <StepCommitmentCheckpoint />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="governance-review">
          <StepGovernanceReview />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="compass-review">
          <StepCompassReview />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="fiduciary-escalation">
          <StepFiduciaryEscalation />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="session-recorded">
          <StepSessionRecorded />
        </Stepper.Step>
      </Stepper.Wrapper>
    </div>
  );
};

export default RecommendationEngine;

function StepRecommendation() {
  const { t } = useTranslation();
  const location = useLocation();
  const { nextStep, setContextData, contextData } = useStepperContext<typeof steps, RecommendationEngineData>();
  const d = contextData as Partial<RecommendationEngineData> | null;
  const phase = d?.loadPhase ?? 'interpretation-reveal';
  const recommendation = d?.recommendation;
  const decisionIdFromRoute = (location.state as { decisionId?: string } | null)?.decisionId;
  const decisionId = d?.decision_id ?? decisionIdFromRoute;

  const shouldLoadPreview =
    phase === 'interpretation-reveal' && !recommendation && Boolean(decisionId);

  const {
    data: previewData,
    isPending,
    isFetching,
    isError,
    error: previewQueryError,
    refetch,
  } = useGovernanceCheckPreview(decisionId, { enabled: shouldLoadPreview });

  const previewError = isError
    ? previewQueryError instanceof Error
      ? previewQueryError.message
      : t('recommendationEnginePage.errors.previewFailed')
    : null;

  const isPreviewLoading = shouldLoadPreview && (isPending || isFetching) && !previewData;

  useEffect(() => {
    if (!previewData || recommendation) return;
    setContextData((prev) => ({
      ...prev,
      decision_id: prev?.decision_id ?? decisionId,
      loadPhase: 'strategic-case-ready',
      recommendation: mapGovernanceCheckPreviewToContent(previewData),
      governance_preview: previewData,
      framework_attribution: previewData.framework_attribution,
    }));
  }, [previewData, recommendation, decisionId, setContextData]);

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full min-h-0">
        <div className="flex flex-col gap-2 shrink-0">
          <StepFormTitle>{t('recommendationEnginePage.title')}</StepFormTitle>
          <StepFormSubtitle>{t('recommendationEnginePage.subtitle')}</StepFormSubtitle>
        </div>

        {previewError && (
          <Alert status="error" className="shrink-0">
            <span>{previewError}</span>
            <Button
              size="sm"
              color="ghost"
              className="ml-2"
              disabled={isPreviewLoading}
              onClick={() => void refetch()}
            >
              {t('button.tryAgain')}
            </Button>
          </Alert>
        )}

        {phase === 'interpretation-reveal' && !previewError && (
          <InterpretationReveal isPending={isPreviewLoading} />
        )}

        {phase === 'strategic-case-ready' && recommendation && (
          <StrategicCaseReady
            recommendation={recommendation}
            onAccept={() => nextStep()}
          />
        )}
      </div>
    </BrandCardSideContent>
  );
}

function InterpretationReveal({ isPending }: { isPending: boolean }) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-6 py-12 min-h-[320px]">
      <Loading size="lg" className={`text-[#0F1B2A] ${isPending ? '' : 'opacity-40'}`} />
      <p className="font-dm text-sm font-semibold text-black/70 text-center">
        {t('recommendationEnginePage.reveal.shaping')}
      </p>
      <ul className="flex flex-col gap-2 items-center">
        {LOADER_STATUS_KEYS.map((key) => (
          <li key={key} className="font-dm text-sm text-black/45 text-center">
            {t(`recommendationEnginePage.reveal.status.${key}`)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StrategicCaseReady({
  recommendation,
  onAccept,
}: {
  recommendation: GovernedRecommendationContent;
  onAccept: () => void;
}) {
  const { t } = useTranslation();
  const frameworkMeta = FRAMEWORK_BY_ID[recommendation.framework_id as FrameworkId];
  const constraintDisplay = formatConstraintSummary(recommendation.constraint_summary, (key) =>
    t(`commonIntakePage.clarify.${key}`),
  );

  return (
    <div className="flex flex-col flex-1 gap-5 w-full min-h-0 overflow-y-auto">
      <div className="rounded-2xl border border-[rgba(0,0,0,0.12)] bg-white p-4 flex flex-col gap-3">
        <SummaryRow
          icon={<DocumentTextIcon className="w-5 h-5 text-black/70 shrink-0" />}
          text={recommendation.issue_title}
        />
        <SummaryRow
          icon={<MagnifyingGlassIcon className="w-5 h-5 text-black/70 shrink-0" />}
          text={constraintDisplay}
        />
        <div className="flex flex-row items-center gap-2 font-dm text-sm text-black/80">
          <CubeTransparentIcon className="w-5 h-5 shrink-0" />
          <span>
            {t('recommendationEnginePage.ready.frameworkLabel')}{' '}
            <strong>{frameworkMeta?.label ?? recommendation.framework_id}</strong>
          </span>
        </div>
      </div>

      <RecommendationSection
        title={t('recommendationEnginePage.sections.recommendedDirection')}
        lead={recommendation.recommended_direction.lead}
        detail={recommendation.recommended_direction.detail}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.rootCause')}
        lead={recommendation.root_cause.lead}
        detail={recommendation.root_cause.detail}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.actionPlan')}
        lead={recommendation.action_plan.lead}
        bullets={recommendation.action_plan.bullets}
        bulletsLabel={t('recommendationEnginePage.sections.immediateActions')}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.expectedImpact')}
        bullets={recommendation.expected_impact}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.risksWatchouts')}
        bullets={recommendation.risks_watchouts}
      />
      <RecommendationSection
        title={t('recommendationEnginePage.sections.next30Days')}
        bullets={recommendation.next_30_days}
      />

      <p className="font-dm text-xs italic text-black/50 px-2">
        {t('recommendationEnginePage.ready.frameworkFootnote')}
      </p>

      <StepFormActions
        onBack={() => undefined}
        submitType="button"
        onContinue={onAccept}
        continueLabel={t('recommendationEnginePage.ready.acceptContinue')}
        secondaryAction={<span />}
      />
    </div>
  );
}

function SummaryRow({ icon, text }: { icon: ReactNode; text: string }) {
  return (
    <div className="flex flex-row items-start gap-2">
      {icon}
      <p className="font-dm text-sm text-black/80 leading-relaxed">{text}</p>
    </div>
  );
}

function RecommendationSection({
  title,
  lead,
  detail,
  bullets,
  bulletsLabel,
}: {
  title: string;
  lead?: string;
  detail?: string;
  bullets?: string[];
  bulletsLabel?: string;
}) {
  return (
    <div className="rounded-2xl bg-[#F4F9FF] p-5 flex flex-col gap-2 font-dm text-sm text-black/70 leading-relaxed">
      <p className="font-semibold text-black/85">{title}</p>
      {lead && <p>{lead}</p>}
      {detail && <p className="text-black/60">{detail}</p>}
      {bullets && bullets.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {bulletsLabel && <p className="font-semibold text-black/75">{bulletsLabel}</p>}
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {bullets.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
